import { QRCodeCanvas } from 'qrcode.react';
import { X as Closed, X } from 'lucide-react';
import { useState } from 'react';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { copyHandler } from '@/lib/handle';
import { useAppStore } from '@/store';

interface IProps {}

export default (_: IProps) => {
  const { depositOpen, fwClass, setDepositOpen } = useAppStore();
  const { walletAddress, chain } = useBoomWallet();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const onCopy = () => {
    if (walletAddress) {
      setIsOpen(true);
      copyHandler(walletAddress);
      setTimeout(() => {
        setIsOpen(false);
      }, 2500);
    }
  };

  return (
    <Sheet open={depositOpen} onOpenChange={(open: boolean) => setDepositOpen(open)}>
      <SheetContent
        side="bottom"
        hideCloseIcon
        className="w-full overflow-y-auto p-[16px] top-[100px] border-t-[1px] border-t-borderColor flex flex-col justify-between"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div>
          <SheetHeader>
            <SheetTitle>
              <div className="flex justify-between mb-[16px]">
                <span>{t('Base.depositFund')}</span>
                <Closed className=" absolute top-[15px] right-[15px]" onClick={() => setDepositOpen(false)} />
              </div>
            </SheetTitle>
          </SheetHeader>
          <div className="flex justify-center">
            <div className="border-[1px] border-borderColor bg-[#fff] p-[22px] rounded-[12px]">
              <QRCodeCanvas size={165} value={walletAddress || ''} />
            </div>
          </div>
          <p className="text-secondaryTextColor text-center my-[8px]">{t('Base.depositFundText')}</p>
          <p className="text-buyColor text-center mb-[16px]">{t('Base.depositFundTextTips', { chain: chain === 'bsc' ? 'BSC' : 'Solana' })}</p>
          <div className="bg-[#F4F4F5CC] dark:bg-borderColor border-borderColor  border-[1px] rounded-[8px] px-[10px] py-[6px] flex gap-x-[4px] items-center">
            <span className={cn('break-all text-left text-[16px]', fwClass)}>{walletAddress}</span>
          </div>
        </div>
        <div className={'flex flex-col gap-y-[11px] text-[14px]'}>
          <Button className="w-full h-[48px] rounded-[12px] !bg-themeColor text-[#09090B] flex items-center" onClick={onCopy}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
              <path
                d="M8.00101 3.07617H2.9995C1.34292 3.07617 0 4.4191 0 6.07568V11.0772C0 12.7338 1.34292 14.0767 2.9995 14.0767H8.00101C9.65759 14.0767 11.0005 12.7338 11.0005 11.0772V6.07568C11.0005 4.4191 9.65759 3.07617 8.00101 3.07617Z"
                fill="#09090B"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.16504 2.09217C3.27471 2.08051 3.38671 2.07584 3.49987 2.07584H8.50021C9.42847 2.07584 10.3187 2.44459 10.9751 3.10097C11.6315 3.75735 12.0002 4.64759 12.0002 5.57585V10.5762C12.0002 10.6894 11.9944 10.8014 11.9839 10.911C12.5728 10.7064 13.0833 10.3235 13.4446 9.81547C13.8059 9.30741 14 8.69944 13.9999 8.07602V3.07568C13.9999 2.28016 13.6839 1.51722 13.1214 0.954706C12.5588 0.39219 11.7959 0.0761719 11.0004 0.0761719L6.00004 0.0761719C4.68754 0.0761719 3.57337 0.918506 3.16504 2.09217Z"
                fill="#09090B"
              />
            </svg>
            <span className={fwClass}>{t('Base.copyAddress')}</span>
          </Button>
          <Button
            className={cn('w-full h-[48px] rounded-[12px] !bg-cardBgColor !text-primaryTextColor', fwClass)}
            onClick={() => setDepositOpen(false)}
          >
            {t('Base.cancel')}
          </Button>
        </div>
      </SheetContent>
      {isOpen && (
        <div className="bg-white dark:bg-[#27272A] text-primaryTextColor border-[1px] border-borderColor rounded-[12px]  px-[12px] w-[96vw] flex items-center justify-between h-[48px] z-[100] absolute top-[10px] left-[50%] translate-x-[-50%]">
          <div className="flex items-center gap-x-[12px]">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
              <circle cx="11.9998" cy="12.4764" r="10.0038" fill="#45A77B" />
              <path d="M8.03172 12.5297L10.5802 15.2939L16.4366 9.54395" stroke="white" strokeWidth="1.5" />
            </svg>
            <span className={cn('text-[14px]', fwClass)}>{t('Base.copySuccessful')}</span>
          </div>
          <X className="text-[#09090B80] dark:text-[#FAFAFA80]" onClick={() => setIsOpen(false)} />
        </div>
      )}
    </Sheet>
  );
};
